import { ChevronDown } from 'lucide-react';
import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { Link } from 'react-router-dom';
import type { IRoute } from '@/routes';
import { cn } from '@/lib/utils';
import { t } from 'i18next';
import { getRegionConfig } from '@/utils/helpers/region';
import { CustomIcon } from '../CustomIcon';

interface CustomSidebarItemProps {
  route: IRoute extends { name: string } ? IRoute : IRoute & { name: string };
  isActive: (path: string) => boolean;
  isSubActive: (path: string) => boolean;
  openMenus: string[];
  toggleMenu: (path: string) => void;
}

export default function CustomSidebarItem({
  route,
  isActive,
  isSubActive,
  openMenus,
  toggleMenu,
}: CustomSidebarItemProps) {
  const region = getRegionConfig();
  const hasSubItems = route.subItems && route.subItems.length > 0;
  const active = isActive(route.path);
  const isOpen = openMenus.includes(route.path);
  const { isMobile, setOpenMobile } = useSidebar();

  return (
    <SidebarMenuItem key={route.path}>
      <SidebarMenuButton
        asChild={!hasSubItems}
        tooltip={t(`routes.${route.nameKey}`)}
        className={cn('group', active && 'bg-transparent', 'w-full')}
        onClick={() => toggleMenu(route.path)}
      >
        {hasSubItems ? (
          <div className="flex items-center justify-between w-full cursor-pointer py-5">
            <div className="flex items-center gap-2">
              {route.icon && (
                <CustomIcon
                  name={route.icon}
                  className={cn(active && 'text-primary')}
                />
              )}
              <span className={cn(active && 'text-primary', 'text-sm')}>
                {route.name}
              </span>
            </div>
            <ChevronDown
              className={cn(
                'ml-auto size-4 transition-transform duration-200 text-muted-foreground',
                isOpen && 'rotate-180',
                active && 'text-primary'
              )}
            />
          </div>
        ) : (
          <Link
            to={route.path}
            className="flex items-center gap-3 w-full py-5"
            onClick={() => {
              if (isMobile) {
                setOpenMobile(false);
              }
            }}
          >
            {route.icon && (
              <CustomIcon
                name={route.icon}
                className={cn(active && 'text-primary')}
              />
            )}
            <span className={cn(active && 'text-primary', 'text-sm')}>
              {route.name}
            </span>
          </Link>
        )}
      </SidebarMenuButton>
      {hasSubItems && isOpen && (
        <SidebarMenuSub>
          {route.subItems?.map((subItem: { path: string; nameKey: string }) => (
            <SidebarMenuSubItem key={subItem.path}>
              <SidebarMenuSubButton
                asChild
                className={cn(
                  isSubActive(subItem.path) && 'bg-transparent',
                  'text-sm py-5'
                )}
              >
                <Link
                  to={subItem.path}
                  onClick={() => {
                    if (isMobile) {
                      setOpenMobile(false);
                    }
                  }}
                >
                  <span
                    className={cn(
                      isSubActive(subItem.path) && 'text-primary',
                      'text-sm'
                    )}
                  >
                    {t(`routes.${subItem.nameKey}`, {
                      brand: region.name,
                    })}
                  </span>
                </Link>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
          ))}
        </SidebarMenuSub>
      )}
    </SidebarMenuItem>
  );
}
